import React from "react";
import {
  Alert,
  Box,
  Card,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  BusinessCenter as BusinessCenterIcon,
  Lock as LockIcon,
  Shield as ShieldIcon,
  VerifiedUser as VerifiedUserIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { getInitialIras } from "../../../contexts/services/iras/irasAuthServices";
import SingpassCorppassLogin from "./SingpassCorppassLogin";
import {
  CompactIrasAuthCard,
  useIrasAuthRedirect,
} from "./IrasAuthCardShared";

const CorpPassAuthCard = ({ company, compact = false }) => {
  const { authStatus, handleAuth, isLoading } = useIrasAuthRedirect({
    company,
    getInitialAuth: getInitialIras,
    logLabel: "CorpPass authentication",
    fallbackErrorMessage: "Failed to connect to IRAS. Please try again.",
  });

  const connectionLabel =
    authStatus === "connecting"
      ? "Redirecting securely"
      : authStatus === "error"
        ? "Connection required"
        : "Not connected";

  if (compact) {
    return (
      <CompactIrasAuthCard
        icon={<AccountBalanceIcon fontSize='small' />}
        title='Connect as Company'
        description='Access IRAS services for your own company using CorpPass.'
        connectingMessage='Opening the CorpPass authorisation page.'
        errorMessage='We could not start the IRAS connection. Please try again.'
        footerText='Credentials are entered only on the government authorisation page.'
        loginProps={{ authStatus, isLoading, onClick: handleAuth }}
      />
    );
  }

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        width: "100%",
        overflow: "hidden",
        borderRadius: 2,
        border: "1px solid",
        borderColor: theme.palette.divider,
        boxShadow: `0 18px 50px ${alpha(theme.palette.common.black, 0.1)}`,
        bgcolor: "#fff",
      })}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" },
          minHeight: { md: 430 },
        }}
      >
        <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <Stack spacing={3}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent='space-between'
            >
              <Box
                sx={(theme) => ({
                  width: 52,
                  height: 52,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                })}
              >
                <AccountBalanceIcon fontSize='medium' />
              </Box>

              <Chip
                icon={<ShieldIcon />}
                label={connectionLabel}
                color={authStatus === "error" ? "error" : "default"}
                variant='outlined'
                sx={{
                  height: 32,
                  fontWeight: 600,
                  bgcolor: "#fff",
                  "& .MuiChip-icon": { fontSize: 18 },
                }}
              />
            </Stack>

            <Box>
              <Typography
                variant='overline'
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontWeight: 700,
                  letterSpacing: 0,
                })}
              >
                IRAS CorpPass Connection
              </Typography>
              <Typography
                variant='h4'
                sx={(theme) => ({
                  mt: 0.5,
                  color: theme.palette.text.primary,
                  fontWeight: 700,
                  fontSize: { xs: "1.75rem", md: "2.2rem" },
                  letterSpacing: 0,
                  lineHeight: 1.18,
                })}
              >
                Authorise secure tax submissions for{" "}
                {company?.name || "this company"}
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  mt: 2,
                  maxWidth: 560,
                  color: "text.secondary",
                  lineHeight: 1.7,
                }}
              >
                Connect through CorpPass before submitting corporate tax
                information to IRAS. Tax Savvy will only receive the
                authorisation token needed for approved IRAS services.
              </Typography>
            </Box>

            {authStatus === "connecting" && (
              <Alert severity='info' variant='outlined'>
                Opening the CorpPass authorisation page. Keep this browser tab
                available while IRAS completes the redirect.
              </Alert>
            )}

            {authStatus === "error" && (
              <Alert severity='error' variant='outlined'>
                We could not start the IRAS connection. Please retry, or check
                that pop-up and redirect controls are not blocking the flow.
              </Alert>
            )}

            <Stack spacing={1.5}>
              <Box sx={{ width: { xs: "100%", sm: 300 }, mb: 1 }}>
                <SingpassCorppassLogin
                  isLoading={isLoading}
                  onClick={handleAuth}
                />
              </Box>

              <Typography
                variant='caption'
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: "text.secondary",
                }}
              >
                <LockIcon
                  sx={(theme) => ({
                    fontSize: 16,
                    color: theme.palette.success.main,
                  })}
                />
                CorpPass credentials are entered only on the government
                authorisation page.
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={(theme) => ({
            p: { xs: 3, sm: 4, md: 5 },
            bgcolor: theme.palette.grey[50],
            borderLeft: { md: `1px solid ${theme.palette.divider}` },
            height: "100%",
          })}
        >
          <Stack spacing={3} sx={{ height: "100%" }}>
            <Box>
              <Typography
                variant='subtitle1'
                sx={(theme) => ({
                  color: theme.palette.text.primary,
                  fontWeight: 700,
                  mb: 1,
                })}
              >
                What this enables
              </Typography>
              <Typography
                variant='body2'
                sx={{ color: "text.secondary", lineHeight: 1.65 }}
              >
                A verified IRAS session for company-level filing workflows, with
                state validation during the OAuth callback.
              </Typography>
            </Box>

            <Divider />

            <Stack spacing={2.5}>
              {[
                {
                  icon: <VerifiedUserIcon />,
                  title: "Verified company authority",
                  description:
                    "CorpPass confirms the authorised user before IRAS access is granted.",
                },
                {
                  icon: <AssignmentTurnedInIcon />,
                  title: "Submission-ready access",
                  description:
                    "Use the connected session for corporate tax filing actions.",
                },
                {
                  icon: <BusinessCenterIcon />,
                  title: "Company context retained",
                  description:
                    "The selected company is restored when the external OAuth flow returns.",
                },
              ].map((item) => (
                <Stack
                  key={item.title}
                  direction='row'
                  spacing={1.5}
                  alignItems='flex-start'
                >
                  <Box
                    sx={(theme) => ({
                      mt: 0.25,
                      width: 34,
                      height: 34,
                      flex: "0 0 34px",
                      borderRadius: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      "& svg": { fontSize: 20 },
                    })}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant='body2'
                      sx={(theme) => ({
                        color: theme.palette.text.primary,
                        fontWeight: 700,
                      })}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.55,
                        mt: 0.35,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Card>
  );
};

export default CorpPassAuthCard;
